import Badge from "common/Badge";
import DataTable_ from "common/DataTable";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAll } from "utils/queries";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { FiEye, FiMoreVertical, FiMail } from "react-icons/fi";
import styles from "./orders.module.css";
import { LoaderIcon } from "react-hot-toast";
import SelectSearch from "common/SelectSearch";
import { SearchOptions, postData } from "utils/queries";
import PopUpModal from "common/Popup";
import Button from "common/Button";
import toast from "react-hot-toast";

const REFUND_STATUS_OPTIONS = [
  { value: "refunded", label: "Refunded" },
  { value: "initiated", label: "Refund Initiated" },
];

const Orders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFromUrl = useMemo(() => {
    const refundParam = searchParams.get("refundStatus");
    return {
      search: searchParams.get("q") || "",
      event: searchParams.get("eventId")
        ? {
            value: searchParams.get("eventId"),
            label: searchParams.get("eventLabel") || "",
          }
        : null,
      refundStatus: refundParam
        ? REFUND_STATUS_OPTIONS.find((o) => o.value === refundParam) || null
        : null,
      page: Math.max(1, Number(searchParams.get("page")) || 1),
      perPage: Math.max(1, Number(searchParams.get("perPage")) || 10),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(initialFromUrl.search);
  const [allOrders, setAllOrders] = useState([]);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(initialFromUrl.event);
  const [selectedRefundStatus, setSelectedRefundStatus] = useState(
    initialFromUrl.refundStatus
  );
  const [currentPage, setCurrentPage] = useState(initialFromUrl.page);
  const [perPage, setPerPage] = useState(initialFromUrl.perPage);
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [isResending, setIsResending] = useState(false);
  const childRef = useRef();
  const exportMenuRef = useRef(null);
  const emailCacheRef = useRef({});
  const didMountRef = useRef(false);
  const didFilterResetRef = useRef(false);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (searchTerm) next.set("q", searchTerm);
        else next.delete("q");
        if (selectedEvent?.value) {
          next.set("eventId", `${selectedEvent.value}`);
          if (selectedEvent.label) {
            next.set("eventLabel", selectedEvent.label);
          } else {
            next.delete("eventLabel");
          }
        } else {
          next.delete("eventId");
          next.delete("eventLabel");
        }
        if (selectedRefundStatus?.value)
          next.set("refundStatus", selectedRefundStatus.value);
        else next.delete("refundStatus");
        if (currentPage > 1) next.set("page", `${currentPage}`);
        else next.delete("page");
        if (perPage !== 10) next.set("perPage", `${perPage}`);
        else next.delete("perPage");
        return next;
      },
      { replace: true }
    );
  }, [
    searchTerm,
    selectedEvent,
    selectedRefundStatus,
    currentPage,
    perPage,
    setSearchParams,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setIsExportMenuOpen(false);
      }
    };

    if (isExportMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExportMenuOpen]);

  const fetchOrders = useCallback(async ({ limit = 10, offset = 0 }) => {
    try {
      setCurrentPage(Math.floor(offset / Math.max(1, limit)) + 1);
      setPerPage(limit);
      let query = `orders/admin?offset=${offset}&limit=${limit}`;
      if (selectedEvent?.value) {
        query += `&event_id=${selectedEvent.value}`;
      }
      if (selectedRefundStatus?.value) {
        query += `&status=${selectedRefundStatus.value}`;
      }
      const result = await getAll(query);
      const orders = result?.data?.orders || [];
      const total = orders?.[0]?.total_count * 1 || 0;
      setData(orders);
      setTotalCount(total);
      if (offset === 0) {
        setAllOrders((prev) => {
          if (limit >= total && total) {
            return orders;
          }

          if (!prev.length) {
            return orders;
          }

          return prev;
        });
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  }, [selectedEvent?.value, selectedRefundStatus?.value]);

  const fetchAllOrders = useCallback(async () => {
    if (isFetchingAll) {
      return allOrders;
    }

    const totalRecords = totalCount || data.length;

    if (!totalRecords) {
      return allOrders;
    }

    try {
      setIsFetchingAll(true);
      let query = `orders/admin?offset=0&limit=${totalRecords}`;
      if (selectedEvent?.value) {
        query += `&event_id=${selectedEvent.value}`;
      }
      if (selectedRefundStatus?.value) {
        query += `&status=${selectedRefundStatus.value}`;
      }
      const result = await getAll(query);
      const orders = result?.data?.orders || [];
      setAllOrders(orders);
      return orders;
    } catch (error) {
      console.error("Error fetching all orders:", error);
      return allOrders;
    } finally {
      setIsFetchingAll(false);
    }
  }, [allOrders, data.length, isFetchingAll, totalCount, selectedEvent?.value, selectedRefundStatus?.value]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      fetchOrders({
        limit: initialFromUrl.perPage,
        offset: (initialFromUrl.page - 1) * initialFromUrl.perPage,
      });
    } else {
      fetchOrders({ limit: 10, offset: 0 });
      childRef.current?.resetFields?.();
    }
    // Reset allOrders when filters change to force fresh fetch for client search
    setAllOrders([]);
  }, [
    fetchOrders,
    selectedEvent?.value,
    selectedRefundStatus?.value,
    initialFromUrl.page,
    initialFromUrl.perPage,
  ]);

  useEffect(() => {
    if (!didFilterResetRef.current) {
      didFilterResetRef.current = true;
      return;
    }
    childRef.current?.resetFields?.();
    setCurrentPage(1);
    setPerPage(10);
  }, [searchTerm]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredData(data);
      setDisplayCount(totalCount);
    }
  }, [data, totalCount, searchTerm]);

  // Filter data based on search term
  useEffect(() => {
    let isMounted = true;

    const applyFilter = async () => {
      if (!searchTerm) {
        return;
      }

      const sourceData = await (async () => {
        if (allOrders.length === totalCount && totalCount) {
          return allOrders;
        }

        const fullData = await fetchAllOrders();
        if (fullData.length) {
          return fullData;
        }

        return allOrders.length ? allOrders : data;
      })();

      const filtered = sourceData.filter(
        (order) =>
          order.order_id.toString().includes(searchTerm) ||
          order.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (isMounted) {
        setFilteredData(filtered);
        setDisplayCount(filtered.length);
      }
    };

    applyFilter();

    return () => {
      isMounted = false;
    };
  }, [searchTerm, allOrders, data, totalCount, fetchAllOrders]);

  const formatOrderDateTime = (createdAt) => {
    if (!createdAt) {
      return "";
    }

    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const formattedDate = date.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} at ${formattedTime}`;
  };

  const formatOrderItems = (items = []) =>
    items
      ?.map((item) => `${item.quantity} x ${item.name}`)
      .filter(Boolean)
      .join(", ");

  const formatStatus = (status = "") => {
    if (!status) {
      return "";
    }
    const normalized = `${status}`.toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const resolveOrderDetails = useCallback(async (order) => {
    const cacheKey = order.order_id;
    const cachedValue = emailCacheRef.current?.[cacheKey];

    // Return cached value if available
    if (cachedValue) {
      return cachedValue;
    }

    // Try to get details from direct order object first
    const directDetails = {
      email:
        order?.email ||
        order?.user_email ||
        order?.user?.email ||
        order?.customer?.email ||
        order?.billing_email ||
        "",
      basePrice: order?.base_price,
      bookingFee: order?.booking_fee,
      totalPrice: order?.total_price,
    };

    // If we have all required details, cache and return them
    if (
      directDetails.email &&
      directDetails.basePrice !== undefined &&
      directDetails.bookingFee !== undefined
    ) {
      emailCacheRef.current[cacheKey] = directDetails;
      return directDetails;
    }

    // If we're missing any details, fetch them from the API
    try {
      const detailResult = await getAll(`orders/order/${order.order_id}`);
      const items = detailResult?.data?.order || [];
      
      // Calculate totals across all items in the order
      const aggregatedDetails = items.reduce((acc, item) => {
        const qty = Number(item.quantity) || 1;
        const price = Number(item.base_price || item.price || 0);
        const fee = Number(item.booking_fee || 0);
        
        acc.basePrice += price * qty;
        acc.bookingFee += fee * qty;
        acc.totalPrice += (price + fee) * qty;
        return acc;
      }, { basePrice: 0, bookingFee: 0, totalPrice: 0 });

      const resolvedDetails = {
        email:
          items?.[0]?.email ||
          items?.[0]?.user_email ||
          items?.[0]?.user?.email ||
          directDetails.email ||
          "",
        eventName: items?.[0]?.event_name || order?.event_name || "",
        paymentMethod: items?.[0]?.payment_method || order?.payment_method || "",
        basePrice: aggregatedDetails.basePrice || Number(directDetails.basePrice || 0),
        bookingFee: aggregatedDetails.bookingFee || Number(directDetails.bookingFee || 0),
        totalPrice: aggregatedDetails.totalPrice || Number(directDetails.totalPrice || 0),
      };

      // Cache the resolved details
      emailCacheRef.current[cacheKey] = resolvedDetails;
      return resolvedDetails;
    } catch (detailError) {
      console.error(
        "Error fetching order details:",
        order.order_id,
        detailError
      );
      // Return whatever details we have, even if incomplete
      const fallbackDetails = {
        email: directDetails.email,
        eventName: order?.event_name || "",
        paymentMethod: order?.payment_method || "",
        basePrice: directDetails.basePrice || 0,
        bookingFee: directDetails.bookingFee || 0,
        totalPrice: directDetails.totalPrice || 0,
      };
      emailCacheRef.current[cacheKey] = fallbackDetails;
      return fallbackDetails;
    }
  }, []);

  const getExportSourceData = useCallback(async () => {
    if (searchTerm) {
      return filteredData;
    }

    const totalRecords = totalCount || data.length;

    if (!totalRecords) {
      return [];
    }

    if (allOrders.length === totalRecords && totalRecords) {
      return allOrders;
    }

    const fullData = await fetchAllOrders();
    if (fullData?.length) {
      return fullData;
    }

    if (allOrders.length) {
      return allOrders;
    }

    return data;
  }, [allOrders, data, fetchAllOrders, filteredData, searchTerm, totalCount]);

  const getOrdersForExport = useCallback(async () => {
    const sourceData = await getExportSourceData();

    if (!sourceData?.length) {
      return [];
    }

    const ordersWithDetails = await Promise.all(
      sourceData.map(async (order) => {
        const details = await resolveOrderDetails(order);
        return {
          ...order,
          resolvedEmail: details.email,
          event_name: details.eventName,
          payment_method: details.paymentMethod,
          base_price: details.basePrice,
          booking_fee: details.bookingFee,
          total_price: details.totalPrice,
        };
      })
    );

    return ordersWithDetails;
  }, [getExportSourceData, resolveOrderDetails]);

  const openResendModal = async (order) => {
    setSelectedOrder(order);
    setNewEmail("");
    setNewName(order?.user_name || "");
    setResendModalOpen(true);
    
    // Fetch the current recipient email from the API
    try {
      const details = await resolveOrderDetails(order);
      // Update the order with the resolved email
      setSelectedOrder(prev => ({
        ...prev,
        resolvedEmail: details.email
      }));
    } catch (error) {
      console.error("Error fetching order email:", error);
    }
  };

  const closeResendModal = () => {
    setResendModalOpen(false);
    setSelectedOrder(null);
    setNewEmail("");
    setNewName("");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResendEmail = async () => {
    if (!newName || !newName.trim()) {
      toast.error("Please enter a name");
      return;
    }

    if (!newEmail || !newEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    if (!validateEmail(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setIsResending(true);
      const result = await postData("stripe/retry-email", {
        order_id: selectedOrder?.order_id,
        newEmail: newEmail.trim(),
        newName: newName.trim(),
      }, false);

      if (result?.status === 200 || result?.data?.success) {
        toast.success("Email resent successfully!");
        closeResendModal();
      } else {
        toast.error(result?.data?.message || "Failed to resend email");
      }
    } catch (error) {
      console.error("Error resending email:", error);
      toast.error(error?.response?.data?.message || "Failed to resend email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const fetchRefundStatusOptions = async (search = "") => {
    if (!search || search.trim() === "") {
      return REFUND_STATUS_OPTIONS;
    }
    const term = search.toLowerCase();
    return REFUND_STATUS_OPTIONS.filter((o) =>
      o.label.toLowerCase().includes(term)
    );
  };

  const fetchOptions = async (search = "") => {
    try {
      let optionsResult;
      
      if (!search || search.trim() === "") {
        // Load all upcoming events when no search term
        optionsResult = await SearchOptions(`event/events?eventType=upcoming&limit=100&offset=0&currentDate=${new Date().toISOString()}`);
        return optionsResult?.data?.data?.rows?.map((e) => ({
          value: e?.id,
          label: e?.event_name,
        })) || [];
      } else {
        // Use search endpoint when user types
        optionsResult = await SearchOptions(`ticket/search?key=${search}`);
        return optionsResult?.data?.data
          ?.filter((e) =>
            e?.event_name?.toLowerCase().includes(search.toLowerCase())
          )
          ?.map((e) => ({
            value: e?.id,
            label: e?.event_name,
          })) || [];
      }
    } catch (error) {
      console.error("Error fetching event options:", error);
      return [];
    }
  };

  const handleExportToPDF = async () => {
    try {
      setIsExportingPDF(true);
      const ordersForExport = await getOrdersForExport();

      if (!ordersForExport.length) {
        return;
      }

      const doc = new jsPDF({ orientation: "landscape" });
      const headers = [
        "Ordered On",
        "Reference",
        "Event",
        "Payment Method",
        "Total Quantity",
        "Order",
        "Name",
        "Email",
        "Price (GBP)",
        "Booking Fee (GBP)",
        "Total Amount (GBP)",
      ];

      // Helper function to calculate total quantity of all items
      const calculateTotalQuantity = (items) => {
        if (!items?.length) return 0;
        return items.reduce(
          (sum, item) => sum + (parseInt(item.quantity) || 0),
          0
        );
      };

      const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2);
      };

      const rows = ordersForExport.map((order) => [
        formatOrderDateTime(order.created_at),
        order?.order_id ?? "",
        order?.event_name ?? "",
        order?.payment_method ?? "",
        calculateTotalQuantity(order.items).toString(),
        formatOrderItems(order.items),
        order?.user_name ?? "",
        order?.resolvedEmail ?? "",
        formatCurrency(Number(order?.base_price) || 0),
        formatCurrency(Number(order?.booking_fee) || 0),
        formatCurrency(Number(order?.total_price) || 0),
      ]);

      autoTable(doc, {
        head: [headers],
        body: rows,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak",
          cellWidth: "wrap",
        },
        headStyles: {
          fillColor: [0, 0, 0],
          textColor: 255,
          fontStyle: "bold",
        },
        bodyStyles: {
          valign: "top",
          textColor: [0, 0, 0],
        },
        columnStyles: {
          0: { cellWidth: 32 }, // Ordered On
          1: { cellWidth: 18 }, // Reference
          2: { cellWidth: 32 }, // Event
          3: { cellWidth: 20 }, // Payment Method
          4: { cellWidth: 15 }, // Total Quantity
          5: { cellWidth: 35 }, // Order
          6: { cellWidth: 22 }, // Name
          7: { cellWidth: 35 }, // Email
          8: { cellWidth: 15 }, // Price (GBP)
          9: { cellWidth: 15 }, // Booking Fee (GBP)
          10: { cellWidth: 15 }, // Total Amount (GBP)
        },
        margin: { top: 15 },
        didDrawPage: function (data) {
          // Add header
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text("Sales Orders", data.settings.margin.left, 10);

          // Add date
          doc.setFontSize(8);
          const date = new Date().toLocaleDateString("en-GB");
          doc.text(`Generated on: ${date}`, data.settings.margin.left, 5);
        },
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      doc.save(`sales-orders-${timestamp}.pdf`);
    } catch (error) {
      console.error("Error exporting orders to PDF:", error);
    } finally {
      setIsExportingPDF(false);
      setIsExportMenuOpen(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsExportingExcel(true);
      const ordersForExport = await getOrdersForExport();

      if (!ordersForExport.length) {
        return;
      }

      // Helper function to calculate total quantity of all items
      const calculateTotalQuantity = (items) => {
        if (!items?.length) return 0;
        return items.reduce(
          (sum, item) => sum + (parseInt(item.quantity) || 0),
          0
        );
      };

      const rows = ordersForExport.map((order) => ({
        "Ordered On": formatOrderDateTime(order.created_at),
        Reference: order?.order_id ?? "",
        Event: order?.event_name ?? "",
        "Payment Method": order?.payment_method ?? "",
        "Total Quantity": calculateTotalQuantity(order.items),
        Order: formatOrderItems(order.items),
        Name: order?.user_name ?? "",
        Email: order?.resolvedEmail ?? "",
        "Price (GBP)": order?.base_price
          ? Number(order.base_price).toFixed(2)
          : "0.00",
        "Booking Fee (GBP)": order?.booking_fee
          ? Number(order.booking_fee).toFixed(2)
          : "0.00",
        "Total Amount (GBP)": order?.total_price
          ? Number(order.total_price).toFixed(2)
          : "0.00",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Ordered On
        { wch: 15 }, // Reference
        { wch: 30 }, // Event
        { wch: 20 }, // Payment Method
        { wch: 15 }, // Total Quantity
        { wch: 50 }, // Order
        { wch: 30 }, // Name
        { wch: 40 }, // Email
        { wch: 15 }, // Price (GBP)
        { wch: 15 }, // Booking Fee (GBP)
        { wch: 15 }, // Total Amount (GBP)
      ];

      worksheet["!cols"] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `sales-orders-${timestamp}.xlsx`);
    } catch (error) {
      console.error("Error exporting orders to Excel:", error);
    } finally {
      setIsExportingExcel(false);
      setIsExportMenuOpen(false);
    }
  };

  const columns = [
    {
      name: "Ordered On",
      selector: (row) => {
        const date = new Date(row.created_at);

        // Format the date as "Wed 10th May 2023"
        const formattedDate = date.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        // Format the time as "at 00:52"
        const formattedTime = date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });

        // return `${formattedDate}\nat ${formattedTime}`;
        return (
          <div>
            {formattedDate} <br /> at {formattedTime}
          </div>
        );
      },
      windth: "fit-content",
    },
    {
      name: "Reference",
      selector: (row) => row.order_id,
      windth: "fit-content",
    },
    {
      name: "Order",
      cell: (row) => (
        <div className={styles.orderCell}>
          {row.items?.map((items, index) => (
            <div key={index}>
              <span className={styles.quantity}>{items.quantity} x</span>{" "}
              <span className={styles.ticket}>{items.name}</span>
            </div>
          ))}
        </div>
      ),
      windth: "fit-content",
    },
    {
      name: "Name",
      selector: (row) => row.user_name,
      windth: "fit-content",
    },
    {
      name: "Refund Status",
      cell: (row) => {
        const status = row?.refund_status;
        if (!status) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        const badgeStyle =
          status === "refunded"
            ? { background: "#FDECEC", color: "#C62828" }
            : status === "initiated"
            ? { background: "#FFF4E0", color: "#B25E00" }
            : { background: "#EFEFF1", color: "#000" };
        const label =
          status === "refunded"
            ? "Refunded"
            : status === "initiated"
            ? "Refund Initiated"
            : status;
        return <Badge label={label} badgeStylesOverride={badgeStyle} />;
      },
      windth: "fit-content",
    },
    // {
    //   name: "Status",
    //   // selector: (row) => row.order_status,
    //   selector:(row) =>{
    //   const status = row.order_status || '';
    //   return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

    //   },
    //   windth: "fit-content",
    // },
    // {
    //   name: "Refund Status",
    //   selector: (row) => row?.refund_request_status || "_",
    //   windth: "fit-content",
    // },
    {
      name: "Price",
      cell: (row) => (
        <span
          style={{
            color: row.order_status === "active" ? "red" : "inherit",
            fontWeight: row.order_status === "active" ? "600" : "normal",
          }}
        >
          £ {row.total_price}
        </span>
      ),
      windth: "fit-content",
    },
    {
      name: "",
      cell: (row) => (
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <FiEye 
            size={18} 
            style={{ cursor: "pointer" }} 
            onClick={() => navigate(`/orders/details/${row?.order_id}`)}
            title="View Details"
          />
          <FiMail 
            size={18} 
            style={{ cursor: "pointer", color: "#0ad5c9" }} 
            onClick={() => openResendModal(row)}
            title="Resend Email"
          />
        </div>
      ),
      width: "100px",
    },
  ];

  return (
    <>
      <div className={styles.headerRow}>
        <div ref={exportMenuRef} className={styles.exportWrapper}>
          <button
            type="button"
            className={styles.exportTrigger}
            onClick={() => setIsExportMenuOpen((prev) => !prev)}
            disabled={isExportingPDF || isExportingExcel}
            aria-haspopup="menu"
            aria-expanded={isExportMenuOpen}
            aria-label="Export options"
            title="Export"
          >
            <FiMoreVertical size={18} />
          </button>
          {isExportMenuOpen && (
            <div className={styles.exportMenu} role="menu">
              <button
                className={styles.exportMenuButton}
                onClick={handleExportToPDF}
                disabled={isExportingPDF || isExportingExcel}
              >
                {isExportingPDF ? (
                  <>
                    <span className={styles.loader} />
                    <span style={{ marginLeft: "8px" }}>Exporting PDF...</span>
                  </>
                ) : (
                  "Export to PDF"
                )}
              </button>
              <button
                className={styles.exportMenuButton}
                onClick={handleExportToExcel}
                disabled={isExportingPDF || isExportingExcel}
              >
                {isExportingExcel ? (
                  <>
                    <span className={styles.loader} />
                    <span style={{ marginLeft: "8px" }}>
                      Exporting Excel...
                    </span>
                  </>
                ) : (
                  "Export to Excel"
                )}
              </button>
            </div>
          )}
        </div>

        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search by Order ID or User Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>
        {/* 🔽 Dynamic Event Selection Dropdown */}
        <div className={styles.filtersRow}>
          <SelectSearch
            id="event-filter"
            placeholder="Filter by Event"
            value={selectedEvent}
            onChange={(val) => setSelectedEvent(val)}
            loadOptions={fetchOptions}
            formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
          />
          <SelectSearch
            id="refund-status-filter"
            placeholder="Filter by Status"
            value={selectedRefundStatus}
            onChange={(val) => setSelectedRefundStatus(val)}
            loadOptions={fetchRefundStatusOptions}
            formGroupStyleOverride={{ width: "30rem", marginBottom: "0" }}
          />
        </div>

      <DataTable_
        handlefetchData={fetchOrders}
        data={filteredData}
        totalCount={displayCount}
        columns={columns}
        ref={childRef}
        initialPage={initialFromUrl.page}
        initialPerPage={initialFromUrl.perPage}
      />

      {/* Resend Email Modal */}
      <PopUpModal modalIsOpen={resendModalOpen} setModalIsOpen={setResendModalOpen}>
        <div style={{ padding: "1rem", minWidth: "400px" }}>
          <h3 style={{ marginBottom: "1rem", color: "#333", fontSize: "1.8rem" }}>
            Resend  Email
          </h3>
          <p style={{ marginBottom: "0.5rem", color: "#666", fontSize: "1.4rem" }}>
            Order ID: <strong>{selectedOrder?.order_id}</strong>
          </p>
          <p style={{ marginBottom: "1.5rem", color: "#666", fontSize: "1.4rem" }}>
            Current recipient: <strong>{selectedOrder?.resolvedEmail || "Loading..."}</strong>
          </p>
          
          <div style={{ marginBottom: "1.5rem" }}>
            <label 
              htmlFor="newName" 
              style={{ 
                display: "block", 
                marginBottom: "0.5rem", 
                fontWeight: "600",
                fontSize: "1.4rem",
                color: "#4A4A4A"
              }}
            >
              New Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              id="newName"
              type="text"
              placeholder="Enter new name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={isResending}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "1.4rem",
                border: "1px solid #ddd",
                borderRadius: "6px",
                outline: "none",
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isResending) {
                  handleResendEmail();
                }
              }}
            />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label 
              htmlFor="newEmail" 
              style={{ 
                display: "block", 
                marginBottom: "0.5rem", 
                fontWeight: "600",
                fontSize: "1.4rem",
                color: "#4A4A4A"
              }}
            >
              New Email Address <span style={{ color: "red" }}>*</span>
            </label>
            <input
              id="newEmail"
              type="email"
              placeholder="Enter new email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={isResending}
              style={{
                width: "100%",
                padding: "10px",
                fontSize: "1.4rem",
                border: "1px solid #ddd",
                borderRadius: "6px",
                outline: "none",
              }}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isResending) {
                  handleResendEmail();
                }
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
            <Button
              label="Cancel"
              onClick={closeResendModal}
              style={{ 
                backgroundColor: "#888", 
                color: "white",
                opacity: isResending ? 0.6 : 1,
                cursor: isResending ? "not-allowed" : "pointer"
              }}
              disabled={isResending}
            />
            <Button
              label={isResending ? "Sending..." : "Resend "}
              onClick={handleResendEmail}
              style={{ 
                backgroundColor: "#4A90E2", 
                color: "white",
                opacity: isResending ? 0.6 : 1,
                cursor: isResending ? "not-allowed" : "pointer"
              }}
              disabled={isResending}
            />
          </div>
        </div>
      </PopUpModal>
    </>
  );
};

export default Orders;
